import { authContext } from "entities/user";
import { PageLayout } from "page_components/page_layout";
import { AccessDeniedPage } from "page_components/access-denied";
import {
    createRoutesFromElements,
    Route,
    createBrowserRouter,
    RouterProvider
} from "react-router-dom";
import React, { lazy, Suspense, ReactNode, useContext, useMemo } from "react";
import { routes } from "../config/routes";
import styles from "./routes_component.module.scss";

const lazyImport = <T extends React.ComponentType<any>>(
    factory: () => Promise<{ [key: string]: T }>,
    name: string
) => {
    return lazy(() => factory()
        .then((module) => ({ default: module[name] }))
        .catch((err) => {
        const refreshed = sessionStorage.getItem('chunk-reload');
        if (!refreshed) {
            sessionStorage.setItem('chunk-reload', 'true');
            window.location.reload();
        }
        throw err;
    }));
};

export const RoutesComponent = () => {
    const user = useContext(authContext);

    const LazyRootPage = lazyImport(() => import("pages/root"), "RootPage");
    const LazyExternalFormPage = lazyImport(() => import("pages/external_form_page"), "ExternalFormPage");
    const LazyTemplatesPage = lazyImport(() => import("pages/my_templates"), "TemplatesPage");
    const LazyDocumentsPage = lazyImport(() => import("pages/documents"), "DocumentsPage");
    const LazyNeurodocPage = lazyImport(() => import("pages/neurodoc"), "NeurodocPage");
    const LazyWordAddinPage = lazyImport(() => import("pages/word_addin"), "WordAddinPage");

    const rootRoute = useMemo(() => <Route element={<PageLayout isExternal={false}/>}>
        <Route path={routes.root} element={<LazyRootPage/>}/>
        <Route path="*" element={<AccessDeniedPage errorStatus={404}/>}/>
    </Route>, []);

    let routeElements: ReactNode[] = [rootRoute];

    const internalRoute = useMemo(() => <Route element={<PageLayout isExternal={false}/>}>
        <Route path={routes.root} element={<LazyRootPage/>}/>
        <Route path={routes.neurodoc} element={<LazyNeurodocPage/>}/>
        <Route path={routes.myTemplates} element={<LazyTemplatesPage/>}/>
        <Route path={routes.template} element={<LazyTemplatesPage/>}/>
        <Route path={routes.oldTemplates} element={<LazyTemplatesPage/>}/>
        <Route path={routes.oldDocuments} element={<LazyDocumentsPage/>}/>
        <Route path={routes.documents} element={<LazyDocumentsPage/>}/>
        <Route path={routes.document} element={<LazyDocumentsPage/>}/>
        <Route path={routes.iframe} element={<LazyDocumentsPage iframe/>}/>
        <Route path={routes.externalForm} element={<LazyExternalFormPage/>}/>
        <Route path="*" element={<AccessDeniedPage errorStatus={404}/>}/>
    </Route>, []);

    const wordAddinRoute = useMemo(() => (
        <Route path={routes.wordAddin} element={<LazyWordAddinPage/>}/>
    ), []);

    const externalRoute = useMemo(() => <Route element={<PageLayout isExternal={true}/>}>
        <Route path={routes.externalForm} element={<LazyExternalFormPage/>}/>
        <Route path="*" element={<AccessDeniedPage errorStatus={404}/>}/>
    </Route>, []);

    if (user) {
        if (user.isExternal) {
            routeElements = [externalRoute];
        } else {
            routeElements = [internalRoute, wordAddinRoute];
        }
    }

    const router = createBrowserRouter(
        createRoutesFromElements(routeElements)
    );

    return (
        <Suspense fallback={<div className={styles.suspenseContainer}></div>}>
            <RouterProvider router={router}/>
        </Suspense>
    );
};
